import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import api from "../../utils/api";
import { useDispatch } from "react-redux";
import Loader from "../loader/Loader";
import { useNotification } from "../shared/NotificationProvider";

const FeedbackForm = () => {
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm();
  const usabilityIssues = watch("usabilityIssues");
  const safetyConfidence = watch("safetyConfidence");
  const communicationHelpful = watch("communicationHelpful");
  const [showLoader, setShowLoader] = useState(false);
  const notify = useNotification();

  const onSubmit = async (data) => {
    try {
      setShowLoader(true);
      const response = await api.post("/feedback", data);
      reset();
      notify.success(response.data.message);
    } catch (error) {
      // Handle different types of errors
      if (error.response) {
        // Server responded with a status other than 2xx

        notify.error(
          `Failed to submit feedback: ${error.response.data.message || "Something went wrong!"}`
        );
      } else if (error.request) {
        // Request was made but no response received
        notify.error("No response from server. Please try again later.");
      } else {
        // Other errors
        notify.error("An error occurred. Please try again.");
      }
    } finally {
      setShowLoader(false);
    }
  };

  const formCardSx = {
    width: "100%",
    maxWidth: "100%",
    p: { xs: 2, sm: 3, md: 4 },
    borderRadius: "4px",
    backgroundColor: "#001d3d",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  };

  const inputSx = {
    borderRadius: "4px",
    fontSize: "0.9rem",
    "& .MuiOutlinedInput-input": { color: "#fff" },
    "& .MuiOutlinedInput-input::placeholder": { color: "rgba(255,255,255,0.5)", opacity: 1 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.8)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
    "& .MuiSelect-icon": { color: "#fff" },
    "& .MuiOutlinedInput-root": { backgroundColor: "rgba(255,255,255,0.08)" },
  };

  const selectSx = {
    ...inputSx,
    "& .MuiSelect-select": { fontSize: "0.9rem" },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: "#fff",
        color: "#212121",
        borderRadius: "4px",
        mt: 0.5,
        "& .MuiMenuItem-root": { fontSize: "0.9rem" },
      },
    },
  };

  return (
    <>
      {showLoader && <Loader />}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={formCardSx}>
        <Typography variant="h4" gutterBottom sx={{ color: "#fff", fontWeight: 600, fontSize: "1.25rem", wordBreak: "break-word", mb: 2 }}>
          FindMyRoommates Feedback Form
        </Typography>

        <FormControl fullWidth margin="normal" sx={selectSx}>
          <InputLabel>How would you rate your overall experience with the app?</InputLabel>
          <Controller
            name="overallExperience"
            control={control}
            defaultValue=""
            rules={{ required: "Rating is required" }}
            render={({ field }) => (
              <Select
                {...field}
                label="How would you rate your overall experience with the app?"
                error={!!errors.overallExperience}
                MenuProps={menuProps}
              >
              <MenuItem value="">
                <em>Select a rating</em>
              </MenuItem>
              <MenuItem value="1">1 - Poor</MenuItem>
              <MenuItem value="2">2 - Fair</MenuItem>
              <MenuItem value="3">3 - Good</MenuItem>
              <MenuItem value="4">4 - Very Good</MenuItem>
              <MenuItem value="5">5 - Excellent</MenuItem>
            </Select>
          )}
        />
        {errors.overallExperience && (
          <Typography sx={{ color: "rgba(255,200,200,0.95)", fontSize: "0.8rem", mt: 0.5 }}>
            {errors.overallExperience.message}
          </Typography>
        )}
      </FormControl>

      <Controller
        name="likes"
        control={control}
        defaultValue=""
        rules={{ required: "This field is required" }}
        render={({ field }) => (
          <TextField
            {...field}
            label="What did you like most about the app?"
            multiline
            rows={4}
            placeholder="Enter your response here"
            fullWidth
            margin="normal"
            error={!!errors.likes}
            sx={inputSx}
          />
        )}
      />
      {errors.likes && (
        <Typography sx={{ color: "rgba(255,200,200,0.95)", fontSize: "0.8rem", mt: 0.5 }}>{errors.likes.message}</Typography>
      )}

      <Controller
        name="improvements"
        control={control}
        defaultValue=""
        rules={{ required: "This field is required" }}
        render={({ field }) => (
          <TextField
            {...field}
            label="What could be improved?"
            multiline
            rows={4}
            placeholder="Enter your response here"
            fullWidth
            margin="normal"
            error={!!errors.improvements}
            sx={inputSx}
          />
        )}
      />
      {errors.improvements && (
        <Typography sx={{ color: "rgba(255,200,200,0.95)", fontSize: "0.8rem", mt: 0.5 }}>{errors.improvements.message}</Typography>
      )}

      <FormGroup>
        <Controller
          name="usabilityIssues"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} sx={{ color: "rgba(255,255,255,0.7)", "&.Mui-checked": { color: "#fff" } }} />}
              label={<Typography sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.9rem" }}>Did you face any difficulties while using the app?</Typography>}
            />
          )}
        />
      </FormGroup>

      {usabilityIssues && (
        <Controller
          name="usabilityIssueDetails"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              label="Please describe the issue"
              multiline
              rows={4}
              placeholder="Enter the details here"
              fullWidth
              margin="normal"
              sx={inputSx}
            />
          )}
        />
      )}

        <FormControl fullWidth margin="normal" sx={selectSx}>
          <InputLabel>Was it easy to navigate through the app? (1 - Difficult, 5 - Very Easy)</InputLabel>
          <Controller
            name="navigationRating"
            control={control}
            defaultValue=""
            rules={{ required: "Rating is required" }}
            render={({ field }) => (
              <Select
                {...field}
                label="Was it easy to navigate through the app? (1 - Difficult, 5 - Very Easy)"
                error={!!errors.navigationRating}
                MenuProps={menuProps}
              >
              <MenuItem value="">
                <em>Select a rating</em>
              </MenuItem>
              <MenuItem value="1">1</MenuItem>
              <MenuItem value="2">2</MenuItem>
              <MenuItem value="3">3</MenuItem>
              <MenuItem value="4">4</MenuItem>
              <MenuItem value="5">5</MenuItem>
            </Select>
          )}
        />
        {errors.navigationRating && (
          <Typography sx={{ color: "rgba(255,200,200,0.95)", fontSize: "0.8rem", mt: 0.5 }}>{errors.navigationRating.message}</Typography>
        )}
      </FormControl>

        <FormControl fullWidth margin="normal" sx={selectSx}>
          <InputLabel>Did you feel safe and confident while interacting with prospective roommates?</InputLabel>
          <Controller
            name="safetyConfidence"
            control={control}
            defaultValue=""
            rules={{ required: "This field is required" }}
            render={({ field }) => (
              <Select
                {...field}
                label="Did you feel safe and confident while interacting with prospective roommates?"
                error={!!errors.safetyConfidence}
                MenuProps={menuProps}
              >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          )}
        />
        {errors.safetyConfidence && (
          <Typography sx={{ color: "rgba(255,200,200,0.95)", fontSize: "0.8rem", mt: 0.5 }}>{errors.safetyConfidence.message}</Typography>
        )}
      </FormControl>

      {safetyConfidence === "no" && (
        <Controller
          name="safetyConcerns"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              label="What concerns did you face?"
              multiline
              rows={4}
              placeholder="Enter your concerns here"
              fullWidth
              margin="normal"
              sx={inputSx}
            />
          )}
        />
      )}

        <FormControl fullWidth margin="normal" sx={selectSx}>
          <InputLabel>Was the in-app communication feature helpful?</InputLabel>
          <Controller
            name="communicationHelpful"
            control={control}
            defaultValue=""
            rules={{ required: "This field is required" }}
            render={({ field }) => (
              <Select
                {...field}
                label="Was the in-app communication feature helpful?"
                error={!!errors.communicationHelpful}
                MenuProps={menuProps}
              >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          )}
        />
        {errors.communicationHelpful && (
          <Typography sx={{ color: "rgba(255,200,200,0.95)", fontSize: "0.8rem", mt: 0.5 }}>{errors.communicationHelpful.message}</Typography>
        )}
      </FormControl>

      {communicationHelpful === "no" && (
        <Controller
          name="communicationSuggestions"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              label="What can we do to make communication better?"
              multiline
              rows={4}
              placeholder="Enter your suggestions here"
              fullWidth
              margin="normal"
              sx={inputSx}
            />
          )}
        />
      )}

        <FormControl fullWidth margin="normal" sx={selectSx}>
          <InputLabel>Which feature do you use the most?</InputLabel>
          <Controller
            name="favoriteFeature"
            control={control}
            defaultValue=""
            rules={{ required: "This field is required" }}
            render={({ field }) => (
              <Select
                {...field}
                label="Which feature do you use the most?"
                error={!!errors.favoriteFeature}
                MenuProps={menuProps}
              >
              <MenuItem value="">
                <em>Select a feature</em>
              </MenuItem>
              <MenuItem value="Search">Search</MenuItem>
              <MenuItem value="Filters">Filters</MenuItem>
              <MenuItem value="Roommate Posts">Roommate Posts</MenuItem>
              <MenuItem value="Communication">Communication</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </Select>
          )}
        />
        {errors.favoriteFeature && (
          <Typography sx={{ color: "rgba(255,200,200,0.95)", fontSize: "0.8rem", mt: 0.5 }}>{errors.favoriteFeature.message}</Typography>
        )}
      </FormControl>

      <Controller
        name="newFeatures"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            label="What new features would you like to see in the app?"
            multiline
            rows={4}
            placeholder="Enter your suggestions here"
            fullWidth
            margin="normal"
            sx={inputSx}
          />
        )}
      />

        <Controller
          name="additionalComments"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              label="Any other feedback or suggestions?"
              multiline
              rows={4}
              placeholder="Enter your comments here"
              fullWidth
              margin="normal"
              sx={inputSx}
            />
          )}
        />

        <Button
          type="submit"
          variant="contained"
          sx={{
            mt: 3,
            backgroundColor: "#fff",
            color: "#001d3d",
            borderRadius: "4px",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#e0e0e0" },
          }}
        >
          Submit Feedback
        </Button>
      </Box>
    </>
  );
};

export default FeedbackForm;
